import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { OnChangeFn, RowSelectionState } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useDebouncedCallback } from 'use-debounce';
import { isEqual } from 'lodash';
import { Spinner } from 'baseui/spinner';
import { Toggle } from 'ui-kit/Toggle/Toggle';

import { useMutation, useQuery } from '@apollo/client';

import { GetUploadedFilesWithHeaders } from 'query/__generated__/getUploadedFilesWithHeaders';
import { GET_UPLOADED_FILES_WITH_HEADERS } from 'query/getUploadedFilesWithHeaders';
import {
  ArchiveLoanTapes,
  ArchiveLoanTapesVariables,
  UnarchiveLoanTapes,
  UnarchiveLoanTapesVariables,
} from 'mutation/__generated__/archiveLoanTapes';
import { ARCHIVE_LOAN_TAPES, UNARCHIVE_LOAN_TAPES } from 'mutation/archiveLoanTapes';
import { FileState } from '__generated__/globalTypes';
import { useUserSettings } from 'features/pages/portfolio/Dashboard/PoolManager/utils/useUserSettings';

import TapesUploadedTable, { InProgressFile } from './TapesUploadedTable';
import { isArchivable } from './isArchivable';
import TapeSelectionActionBar from './TapeSelectionActionBar';
import { showActionToast, showUndoToast } from './showUndoToast';
import { tapesUploadedPanelClass } from '../loanTapeUploader/LoanTapeUploader.styles';

type UploadHistoryProps = {
  uploadsInProgress: InProgressFile[];
};

const SHOW_ARCHIVED_SETTING = 'show-archived-tapes';

const pluralizeTapes = (n: number): string => `${n} ${n === 1 ? 'tape' : 'tapes'}`;

type ListedTape = GetUploadedFilesWithHeaders['user']['company']['loan_tapes'][number];

function withoutIds<T>(prev: ReadonlyMap<string, T>, ids: string[]): ReadonlyMap<string, T> {
  if (!ids.some((id) => prev.has(id))) return prev;
  const next = new Map(prev);
  ids.forEach((id) => next.delete(id));
  return next;
}

const UploadHistory = (props: UploadHistoryProps) => {
  const { addUserSetting, savedPoolSettings, userPoolSettings } =
    useUserSettings(SHOW_ARCHIVED_SETTING);
  // "Settled" = the settings query has either data or an error. Keying off data alone
  // would wedge the tape list on "Loading…" forever if GetPoolSettings errors (skip
  // never lifts). On error we just fall back to the default (archived hidden).
  const settingsReady = savedPoolSettings.data != null || savedPoolSettings.error != null;

  // includeArchived is local state so the toggle and the tape list flip together on
  // click, instead of the list lagging behind the settings save→refetch round trip
  // (and disagreeing outright if the save fails). Seeded once from the persisted value.
  const [includeArchived, setIncludeArchived] = useState(false);
  // `seeded` gates the first fetch (below). It flips true in the SAME render batch as
  // the seed, so the query never fires with the unseeded `false` default — that would
  // flash the active-only list to a user who saved "Show archived" (ENG-2845 flash guard).
  const [seeded, setSeeded] = useState(false);
  useEffect(() => {
    if (settingsReady && !seeded) {
      setIncludeArchived(userPoolSettings === true);
      setSeeded(true);
    }
  }, [settingsReady, seeded, userPoolSettings]);

  // Undo and Restore have the same wait in reverse: until a list fetched after the restore
  // lands, the list on screen can lack the tape or still show it archived. Show it restored
  // from the last list that had it until such a list answers, whatever that list says.
  const [locallyRestoredTapes, setLocallyRestoredTapes] = useState<ReadonlyMap<string, ListedTape>>(
    () => new Map(),
  );
  const settleLocallyRestored = useCallback(() => {
    setLocallyRestoredTapes((prev) => (prev.size === 0 ? prev : new Map()));
  }, []);

  const { data, loading, error, startPolling, stopPolling, refetch, observable } =
    useQuery<GetUploadedFilesWithHeaders>(GET_UPLOADED_FILES_WITH_HEADERS, {
      variables: { includeArchived },
      fetchPolicy: 'cache-and-network',
      pollInterval: 2000,
      skip: !seeded,
      // A deduplicated refetch joins whichever list fetch is already in flight and returns
      // the list as of before the mutation. Kept separate, it is the newest request, and
      // Apollo keeps an older request's late answer out of the cache.
      context: { queryDeduplication: false },
      // Fires when a fetched list lands for this view. Every restore is followed by its own
      // refetch, so that list was requested after the restore. It does not fire for the
      // cached list shown on a Show archived flip, nor for a failed fetch.
      onCompleted: settleLocallyRestored,
    });

  // useQuery can skip onCompleted when an answer leaves the result unchanged, so a restore's
  // refetch also settles that restore's tapes on its own answer, but only if that is what the
  // view shows (after a Show archived flip it answers the other view) and no later refetch
  // was sent. An older refetch, such as an archive's still in flight at Undo, was requested
  // before the restore and can match the view without having the restored tape.
  const latestRefetch = useRef(0);
  const refetchList = useCallback(
    async (restoredIds: string[] = []) => {
      const request = ++latestRefetch.current;
      const answer = await refetch().catch(() => undefined);
      if (
        request === latestRefetch.current &&
        answer?.data &&
        isEqual(answer.data, observable.getCurrentResult().data)
      ) {
        setLocallyRestoredTapes((prev) => withoutIds(prev, restoredIds));
      }
    },
    [refetch, observable],
  );

  const [archiveLoanTapes] = useMutation<ArchiveLoanTapes, ArchiveLoanTapesVariables>(
    ARCHIVE_LOAN_TAPES,
  );
  const [unarchiveLoanTapes] = useMutation<UnarchiveLoanTapes, UnarchiveLoanTapesVariables>(
    UNARCHIVE_LOAN_TAPES,
  );

  useEffect(() => {
    startPolling(2000);
    return () => {
      stopPolling();
    };
  }, [startPolling, stopPolling]);

  const serverTapes = useMemo(() => data?.user.company.loan_tapes ?? [], [data]);

  const loanTapeIds = useMemo(
    () => serverTapes.map((loanTape) => loanTape.original_id),
    [serverTapes],
  );

  // The Undo toast's window starts when the archive mutation succeeds, but the list only
  // drops the row once a refetch of the whole upload history lands — seconds later on a
  // large history. Hiding what this view archived right away keeps that window usable.
  const [locallyArchivedIds, setLocallyArchivedIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  const lastListedTapes = useRef(new Map<string, ListedTape>());
  useEffect(() => {
    serverTapes.forEach((tape) => lastListedTapes.current.set(tape.id, tape));
  }, [serverTapes]);
  const markLocallyArchived = useCallback((ids: string[]) => {
    setLocallyArchivedIds((prev) => new Set([...prev, ...ids]));
    setLocallyRestoredTapes((prev) => withoutIds(prev, ids));
  }, []);
  const markLocallyRestored = useCallback((ids: string[]) => {
    setLocallyArchivedIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.delete(id));
      return next;
    });
    setLocallyRestoredTapes((prev) => {
      const next = new Map(prev);
      ids.forEach((id) => {
        const tape = lastListedTapes.current.get(id);
        if (tape) next.set(id, { ...tape, archived_at: null });
      });
      return next;
    });
  }, []);

  // Once the active-only list omits a tape the server has caught up, so it owns that row
  // again: a restore from another tab or user must show here on the next poll.
  useEffect(() => {
    if (includeArchived) return;
    const listedIds = new Set(serverTapes.map((t) => t.id));
    setLocallyArchivedIds((prev) => {
      const stillListed = [...prev].filter((id) => listedIds.has(id));
      return stillListed.length === prev.size ? prev : new Set(stillListed);
    });
  }, [includeArchived, serverTapes]);

  const loanTapes = useMemo(() => {
    const listed = includeArchived
      ? serverTapes
      : serverTapes.filter((t) => !locallyArchivedIds.has(t.id));
    if (locallyRestoredTapes.size === 0) return listed;
    const listedIds = new Set(listed.map((t) => t.id));
    return [
      ...listed.map((t) => (locallyRestoredTapes.has(t.id) ? { ...t, archived_at: null } : t)),
      ...[...locallyRestoredTapes.values()].filter((t) => !listedIds.has(t.id)),
    ];
  }, [serverTapes, includeArchived, locallyArchivedIds, locallyRestoredTapes]);

  // Hand-off from step 1 to step 2: when a tape uploaded THIS SESSION finishes
  // parsing, offer a one-click jump to its detail page. Session-scoped (other
  // users'/tabs' uploads stay silent) and suppressed for batch uploads, where
  // per-tape toasts would spam — the status pills carry the affordance there.
  const navigate = useNavigate();
  const toastedReadyIds = useRef(new Set<string>());
  useEffect(() => {
    const sessionIds = new Set(
      props.uploadsInProgress.map((f) => f.id).filter((id): id is string => id != null),
    );
    if (sessionIds.size === 0) return;
    // Session tapes that just reached "ready to map" and haven't been toasted.
    const newlyReady = serverTapes.filter(
      (tape) =>
        sessionIds.has(tape.original_id) &&
        tape.state === FileState.NEEDS_MAPPING &&
        !toastedReadyIds.current.has(tape.id),
    );
    if (newlyReady.length === 0) return;
    newlyReady.forEach((tape) => toastedReadyIds.current.add(tape.id));
    // "Batch" = more than one tape became ready in THIS pass, not more than one
    // upload this visit: uploadsInProgress accumulates across the session (rows
    // are hidden once persisted, never pruned), so a session-size heuristic
    // would suppress the toast on every upload after the first.
    if (newlyReady.length === 1) {
      const tape = newlyReady[0];
      const tapeName = tape.name ?? 'Tape';
      showActionToast({
        // Two lines: the filename truncates across the full toast width (full
        // name on hover), with the status beneath — so a long title reads
        // instead of collapsing to a couple of characters next to the suffix.
        message: (
          <span className="flex min-w-0 flex-col">
            <span className="truncate font-medium" title={tapeName}>
              {tapeName}
            </span>
            <span className="text-sm text-slate-300">Parsed — ready to map</span>
          </span>
        ),
        actionLabel: 'Map fields →',
        onAction: () => navigate(`/portfolio/tape-upload/${tape.id}`),
        testId: 'map-fields-toast-button',
      });
    }
  }, [serverTapes, props.uploadsInProgress, navigate]);

  // ---- Bulk selection (ENG-2845 follow-up) ------------------------------------

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const onSelectionChange: OnChangeFn<RowSelectionState> = useCallback(
    (updater) =>
      setRowSelection((prev) => (typeof updater === 'function' ? updater(prev) : updater)),
    [],
  );
  const tableSelection = useMemo(
    () => ({ selected: rowSelection, onSelectionChange }),
    [rowSelection, onSelectionChange],
  );

  // Prune selections the data no longer backs: rows that left the list (toggle
  // turned off, archived elsewhere) or transitioned out of a selectable state.
  useEffect(() => {
    const selectableIds = new Set(loanTapes.filter(isArchivable).map((t) => t.id));
    setRowSelection((prev) => {
      const next = Object.fromEntries(
        Object.entries(prev).filter(([id, selected]) => selected && selectableIds.has(id)),
      );
      return Object.keys(next).length === Object.keys(prev).length ? prev : next;
    });
  }, [loanTapes]);

  const { activeIds, archivedIds } = useMemo(() => {
    const byId = new Map(loanTapes.map((t) => [t.id, t]));
    const selectedIds = Object.keys(rowSelection).filter((id) => rowSelection[id]);
    return {
      // Require the tape to still be in the list: a selected id that left
      // before the prune effect ran must count as neither active nor archived.
      // `byId.get(id)?.archived_at == null` is true for a missing id (undefined
      // == null), which would wrongly offer Archive for a departed row.
      activeIds: selectedIds.filter((id) => {
        const tape = byId.get(id);
        return tape != null && tape.archived_at == null;
      }),
      archivedIds: selectedIds.filter((id) => byId.get(id)?.archived_at != null),
    };
  }, [rowSelection, loanTapes]);

  const selectionActive = activeIds.length + archivedIds.length > 0;

  const clearSelection = useCallback(() => setRowSelection({}), []);

  // ---- Toggle persistence ------------------------------------------------------

  // Debounce the persist so a flurry of toggles collapses to a single write of the final
  // value — independent writes could otherwise resolve out of order and leave the server
  // disagreeing with the switch. Only revert if the UI still shows the value we tried to
  // save (the user may have toggled again before the failure landed).
  const persistShowArchived = useDebouncedCallback((checked: boolean) => {
    addUserSetting(JSON.stringify(checked)).catch(() => {
      setIncludeArchived((current) => (current === checked ? !checked : current));
      toast.error('Could not save your preference');
    });
  }, 400);

  // Flush a pending write if the user navigates away before the debounce fires.
  useEffect(() => () => persistShowArchived.flush(), [persistShowArchived]);

  const handleToggle = (checked: boolean) => {
    // Flip the list immediately so it agrees with the switch; persist (debounced) after.
    setIncludeArchived(checked);
    persistShowArchived(checked);
  };

  // ---- Archive / restore with Undo (no confirm dialog) -------------------------
  //
  // Archive is fully reversible (Show archived → Restore), so the action applies
  // immediately and the toast carries an Undo that calls the inverse mutation with
  // the ids that actually succeeded. Per-tape results gate success — the batch's
  // top-level flag is unreliable for partial failures.

  const undoArchive = useCallback(
    async (ids: string[]) => {
      try {
        const result = await unarchiveLoanTapes({ variables: { input: { tape_ids: ids } } });
        const results = result.data?.unarchiveLoanTapes?.results;
        const restored = (results ?? []).filter((r) => r.success).map((r) => r.tape_id);
        markLocallyRestored(restored);
        const allOk = results?.every((r) => r.success);
        if (!allOk) toast.error('Could not undo');
        await refetchList(restored);
      } catch {
        toast.error('Could not undo');
      }
    },
    [unarchiveLoanTapes, refetchList, markLocallyRestored],
  );

  const undoRestore = useCallback(
    async (ids: string[]) => {
      try {
        const result = await archiveLoanTapes({ variables: { input: { tape_ids: ids } } });
        const results = result.data?.archiveLoanTapes?.results;
        markLocallyArchived((results ?? []).filter((r) => r.success).map((r) => r.tape_id));
        const allOk = results?.every((r) => r.success);
        if (!allOk) toast.error('Could not undo');
        await refetchList();
      } catch {
        toast.error('Could not undo');
      }
    },
    [archiveLoanTapes, refetchList, markLocallyArchived],
  );

  const bulkArchive = useCallback(
    async (ids: string[]) => {
      clearSelection();
      try {
        const result = await archiveLoanTapes({ variables: { input: { tape_ids: ids } } });
        const results = result.data?.archiveLoanTapes?.results ?? [];
        const succeeded = results.filter((r) => r.success).map((r) => r.tape_id);
        const failed = results.filter((r) => !r.success);
        if (succeeded.length > 0) {
          markLocallyArchived(succeeded);
          showUndoToast({
            message: `Archived ${pluralizeTapes(succeeded.length)}`,
            onUndo: () => {
              void undoArchive(succeeded);
            },
          });
        }
        if (failed.length > 0) {
          toast.error(failed[0]?.message || `Could not archive ${pluralizeTapes(failed.length)}`);
        }
        // Reflect the change immediately; the 2s poll is the backstop, so a failed
        // refetch must not surface as an archive failure.
        await refetchList();
      } catch {
        toast.error('Could not archive tapes');
      }
    },
    [archiveLoanTapes, clearSelection, refetchList, undoArchive, markLocallyArchived],
  );

  const bulkRestore = useCallback(
    async (ids: string[]) => {
      clearSelection();
      try {
        const result = await unarchiveLoanTapes({ variables: { input: { tape_ids: ids } } });
        const results = result.data?.unarchiveLoanTapes?.results ?? [];
        const succeeded = results.filter((r) => r.success).map((r) => r.tape_id);
        const failed = results.filter((r) => !r.success);
        if (succeeded.length > 0) {
          markLocallyRestored(succeeded);
          showUndoToast({
            message: `Restored ${pluralizeTapes(succeeded.length)}`,
            onUndo: () => {
              void undoRestore(succeeded);
            },
          });
        }
        if (failed.length > 0) {
          toast.error(failed[0]?.message || `Could not restore ${pluralizeTapes(failed.length)}`);
        }
        await refetchList(succeeded);
      } catch {
        toast.error('Could not restore tapes');
      }
    },
    [unarchiveLoanTapes, clearSelection, refetchList, undoRestore, markLocallyRestored],
  );

  return (
    <div className={tapesUploadedPanelClass}>
      <>
        {/* Fixed-height slot: the selection bar swaps in place of the header
            row (Gmail-style) instead of rendering below it, so going 0→1
            selected never pushes the table down. Both states fill the same
            height — no layout shift. */}
        <div className="mb-3 flex h-[44px] items-center">
          {selectionActive ? (
            <TapeSelectionActionBar
              activeIds={activeIds}
              archivedIds={archivedIds}
              onArchive={bulkArchive}
              onRestore={bulkRestore}
              onClear={clearSelection}
            />
          ) : (
            <div className="flex w-full items-center justify-between">
              <div className="uppercase text-slate-200">
                TAPES UPLOADED {loading && <Spinner size={12} />}
              </div>
              {seeded && (
                <div data-testid="show-archived-toggle">
                  {/* key on includeArchived so the uncontrolled Toggle re-seeds to the
                      current value — keeps the switch in lockstep, incl. revert-on-failure. */}
                  <Toggle
                    key={`show-archived-${includeArchived}`}
                    labelPosition="left"
                    checkedLabel="Show archived"
                    uncheckedLabel="Show archived"
                    initialState={includeArchived}
                    onChange={handleToggle}
                  />
                </div>
              )}
            </div>
          )}
        </div>
        <TapesUploadedTable
          inProgressFiles={props.uploadsInProgress.filter(
            (file) => file.id == null || !loanTapeIds?.includes(file.id),
          )}
          tableRows={data ? loanTapes : undefined}
          error={error}
          selection={tableSelection}
        />
      </>
    </div>
  );
};

export type { InProgressFile, UploadHistoryProps };

export default UploadHistory;
